import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, DatePicker, Space, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import styled from 'styled-components';
import { apis } from 'utils/lib/apis';
import { logToSentry } from 'utils/networkErrorUtils';

interface UsageRow {
    featureKey: string;
    model?: string | null;
    callCount: number;
    promptTokens?: number;
    completionTokens?: number;
    updatedAt?: string;
    usageLabel?: string;
}

const FEATURE_TITLE: Record<string, string> = {
    'vocabularyRDS-prod-storeMediaForStory': 'Story media (Soy Historias)',
};

type FeatureRule = { match: (k: string) => boolean; title: string };

const FEATURE_RULES: FeatureRule[] = [
    {
        match: (k) =>
            k.includes('owe-express') &&
            (k.includes('StoryAI') || k.includes('story-ai-stream')),
        title: '1Way story',
    },
    {
        match: (k) =>
            k.includes('assignment-submissions') && k.includes('ai_feedback'),
        title: 'Assignment — AI feedback & suggestions',
    },
    {
        match: (k) =>
            k.includes('assignment-submissions') && k.includes('ai_grading'),
        title: 'Assignment — AI grading',
    },
    {
        match: (k) =>
            k.includes('owe-express') &&
            (k.includes('generate-questions-stream') ||
                k.includes('generate_questions') ||
                k.includes('generate-questions')),
        title: 'AI — generate questions (stream)',
    },
];

function titleForFeature(rawKey: string): string {
    const fromRule = FEATURE_RULES.find((r) => r.match(rawKey));
    if (fromRule) {
        return fromRule.title;
    }
    if (FEATURE_TITLE[rawKey]) {
        return FEATURE_TITLE[rawKey];
    }
    const storyScoped = rawKey.match(/^(.+storeMediaForStory):(.+)$/);
    if (storyScoped) {
        const step = storyScoped[2];
        if (step === 'gpt_image_prompts') {
            return 'Story media — GPT (3 DALL·E prompts from story)';
        }
        if (step === 'dalle_image_generation') {
            return 'Story media — DALL·E HD (1 call per rendered image)';
        }
        return `Story media — ${step.replace(/_/g, ' ')}`;
    }
    const expressMode = rawKey.match(/^owe-express:([^:]+):(.+)$/);
    if (expressMode) {
        const env = expressMode[1];
        const route = expressMode[2].replace(/_/g, '/');
        return `owe-express (${env}) — ${route}`;
    }
    const m = rawKey.match(/^([A-Za-z0-9]+)RDS-prod-(.+)$/);
    if (m) {
        const tail = m[2].replace(/([a-z])([A-Z])/g, '$1 $2');
        return `${m[1]} · ${tail}`;
    }
    return rawKey.replace(/-prod-/g, ' · ');
}

function labelForFeature(rawKey: string): string | undefined {
    const expressMode = rawKey.match(/^owe-express:([^:]+):/);
    if (expressMode) {
        return expressMode[1];
    }
    const prod = rawKey.match(/-prod-/);
    if (prod) {
        return 'prod';
    }
    const stag = rawKey.match(/-stag-|staging/i);
    if (stag) {
        return 'stag';
    }
    return undefined;
}

function isLikelyImageModel(model: string | null | undefined): boolean {
    if (!model || model === 'unknown') {
        return false;
    }
    const lower = model.toLowerCase();
    return lower.includes('dall-e') || lower.includes('gpt-image') || lower === 'dall-e-2' || lower === 'dall-e-3';
}

function formatModelDisplay(model: string | null | undefined): string {
    if (!model || model === 'unknown') {
        return '—';
    }
    if (isLikelyImageModel(model)) {
        return `${model} (images)`;
    }
    return model;
}

function tokensNotApplicableForRow(row: UsageRow): boolean {
    return (
        isLikelyImageModel(row.model) &&
        !(row.promptTokens || 0) &&
        !(row.completionTokens || 0)
    );
}

function tokensShowNa(row: UsageRow): boolean {
    return (
        tokensNotApplicableForRow(row) ||
        ((!row.model || row.model === 'unknown') &&
            !(row.promptTokens || 0) &&
            !(row.completionTokens || 0) &&
            (row.callCount || 0) > 0)
    );
}

function formatUpdatedAtDisplay(iso: string | undefined): string {
    if (!iso || !String(iso).trim()) {
        return '—';
    }
    const d = dayjs(iso);
    if (!d.isValid()) {
        return iso;
    }
    return d.format('MMM D, YYYY, h:mm:ss A');
}

const Section = styled(Card)`
    margin-top: 16px;
`;

const OpenAiUsageReport: React.FC = () => {
    const [date, setDate] = useState<Dayjs>(() => dayjs());
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<UsageRow[]>([]);
    const [totalCalls, setTotalCalls] = useState(0);
    const [totalPromptTokens, setTotalPromptTokens] = useState(0);
    const [totalCompletionTokens, setTotalCompletionTokens] = useState(0);
    const [resolvedDate, setResolvedDate] = useState('');
    const [error, setError] = useState<string | null>(null);

    const usageDateStr = useMemo(() => date.format('YYYY-MM-DD'), [date]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apis.get('permissionsRDS', '/admin/openai-usage/by-day', {
                queryParams: { usageDate: usageDateStr },
            });
            const data = response?.data ?? response;
            const list = Array.isArray(data?.rows) ? data.rows : [];
            setRows(
                list.map((row: UsageRow) => ({
                    ...row,
                    usageLabel: labelForFeature(row.featureKey || ''),
                }))
            );
            setTotalCalls(typeof data?.totalCalls === 'number' ? data.totalCalls : 0);
            setTotalPromptTokens(typeof data?.totalPromptTokens === 'number' ? data.totalPromptTokens : 0);
            setTotalCompletionTokens(
                typeof data?.totalCompletionTokens === 'number' ? data.totalCompletionTokens : 0
            );
            setResolvedDate(typeof data?.usageDate === 'string' ? data.usageDate : usageDateStr);
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Could not load OpenAI usage.';
            setError(msg);
            logToSentry({
                message: 'OpenAI usage report load failed',
                error: err,
                module: 'SUPER_ADMIN',
                operation: 'OPENAI_USAGE_REPORT',
            });
            setRows([]);
            setTotalCalls(0);
            setTotalPromptTokens(0);
            setTotalCompletionTokens(0);
        } finally {
            setLoading(false);
        }
    }, [usageDateStr]);

    useEffect(() => {
        load();
    }, [load]);

    const columns: ColumnsType<UsageRow> = [
        {
            title: 'What it is',
            dataIndex: 'featureKey',
            key: 'featureKey',
            ellipsis: true,
            render: (raw: string) => (
                <>
                    <Typography.Text strong>{titleForFeature(raw)}</Typography.Text>
                    <br />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                        {raw}
                    </Typography.Text>
                </>
            ),
        },
        {
            title: 'Label',
            dataIndex: 'usageLabel',
            key: 'usageLabel',
            width: 88,
            render: (v: string | undefined) =>
                v === 'prod' || v === 'test' ? (
                    <Typography.Text>{v}</Typography.Text>
                ) : (
                    <Typography.Text type="secondary">{v?.trim() ? v : '—'}</Typography.Text>
                ),
        },
        {
            title: 'Model / API',
            dataIndex: 'model',
            key: 'model',
            width: 180,
            ellipsis: true,
            render: (v: string | null | undefined, row) => {
                const label = formatModelDisplay(v);
                const unknownNoTok =
                    (!v || v === 'unknown') &&
                    !(row.promptTokens || 0) &&
                    !(row.completionTokens || 0) &&
                    (row.callCount || 0) > 0;
                const inner = <span>{label}</span>;
                if (tokensNotApplicableForRow(row)) {
                    return (
                        <Tooltip title="Image generations are billed per image; OpenAI often omits token usage on this endpoint.">
                            {inner}
                        </Tooltip>
                    );
                }
                if (unknownNoTok) {
                    return (
                        <Tooltip title="Response had no model field (common for older image logs). Backend now reads the model from the request body for image calls.">
                            <span>unknown</span>
                        </Tooltip>
                    );
                }
                return inner;
            },
        },
        {
            title: 'Calls',
            dataIndex: 'callCount',
            key: 'callCount',
            width: 90,
            sorter: (a, b) => (a.callCount || 0) - (b.callCount || 0),
            sortDirections: ['descend', 'ascend'],
            defaultSortOrder: 'descend',
        },
        {
            title: 'Prompt tokens',
            dataIndex: 'promptTokens',
            key: 'promptTokens',
            width: 130,
            sorter: (a, b) => (a.promptTokens || 0) - (b.promptTokens || 0),
            sortDirections: ['descend', 'ascend'],
            render: (v: number | undefined, row) => {
                const n = typeof v === 'number' ? v : 0;
                if (tokensShowNa(row)) {
                    return <Typography.Text type="secondary">n/a</Typography.Text>;
                }
                return n.toLocaleString();
            },
        },
        {
            title: 'Completion tokens',
            dataIndex: 'completionTokens',
            key: 'completionTokens',
            width: 150,
            sorter: (a, b) => (a.completionTokens || 0) - (b.completionTokens || 0),
            sortDirections: ['descend', 'ascend'],
            render: (v: number | undefined, row) => {
                const n = typeof v === 'number' ? v : 0;
                if (tokensShowNa(row)) {
                    return <Typography.Text type="secondary">n/a</Typography.Text>;
                }
                return n.toLocaleString();
            },
        },
        {
            title: 'Last update',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 220,
            sorter: (a, b) => dayjs(a.updatedAt || 0).valueOf() - dayjs(b.updatedAt || 0).valueOf(),
            sortDirections: ['descend', 'ascend'],
            render: (v: string | undefined) => {
                const label = formatUpdatedAtDisplay(v);
                if (!v || label === '—') {
                    return label;
                }
                return (
                    <Tooltip title={v}>
                        <span>{label}</span>
                    </Tooltip>
                );
            },
        },
    ];

    return (
        <Section title="OpenAI API usage by day">
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
                Per Lambda and model: HTTP calls to OpenAI and token totals when the API returns usage. Chat models show
                real tokens; DALL·E image calls often show n/a for tokens (billed per image). Date bucket is UTC.
            </Typography.Paragraph>
            <Space wrap>
                <span>Date:</span>
                <DatePicker value={date} allowClear={false} onChange={(d) => d && setDate(d)} />
                <Button type="primary" onClick={load} loading={loading}>
                    Refresh
                </Button>
                <Typography.Text type="secondary">
                    Calls: <strong>{totalCalls.toLocaleString()}</strong>
                    {' · '}
                    Prompt tok: <strong>{totalPromptTokens.toLocaleString()}</strong>
                    {' · '}
                    Completion tok: <strong>{totalCompletionTokens.toLocaleString()}</strong>
                    {resolvedDate ? ` — ${resolvedDate}` : ''}
                </Typography.Text>
            </Space>
            {error ? (
                <Alert type="error" message={error} style={{ marginTop: 16 }} showIcon />
            ) : null}
            <Table<UsageRow>
                style={{ marginTop: 16 }}
                loading={loading}
                rowKey={(r) =>
                    `${r.featureKey}|${r.model || ''}|${r.usageLabel || ''}|${r.updatedAt || ''}`
                }
                columns={columns}
                dataSource={rows}
                pagination={{ pageSize: 50, showSizeChanger: false }}
            />
        </Section>
    );
};

export default OpenAiUsageReport;
